/**
 * PushNotifications.store — web push (FCM) enrolment for the current browser.
 *
 * Responsibilities:
 *   1. Describe THIS browser profile's push capability (supported? installed?
 *      permission granted / never asked / blocked?).
 *   2. Keep the server's `push_devices` row current on every app load, granted
 *      or not, so BST can see per-agent readiness instead of sending blind.
 *   3. Obtain and refresh the FCM token when permission is granted.
 *
 * It never prompts on its own. `enable()` must be called from a user gesture —
 * a permission prompt fired on page load gets blanket-denied, and a denial is
 * permanent for that browser.
 *
 * SECURITY — Constraint 2 of the push-notifications plan:
 *   * Registration is hard-skipped while an admin is impersonating an agent
 *     (`UserProfileService.isAssumed()`). This is defence in depth only — the
 *     real guarantee is server-side, where identity comes from the Firebase ID
 *     token, which impersonation never touches.
 *   * A change of Firebase uid tears the token down before re-enrolling, so a
 *     device cannot keep delivering to a previous account.
 */
import { makeAutoObservable, runInAction } from 'mobx'
import { onAuthStateChanged } from 'firebase/auth'
import { getMessaging, getToken, onMessage, deleteToken, isSupported } from 'firebase/messaging'
// The one app and auth instance (docs/REACT-18-MIGRATION.md §1). Never
// firebase/compat — its separate app registry is empty here.
import firebaseApp, { firebaseAuth } from './../../api/FirebaseAuth'
import env from './../../environments/environment'
import StorageService from './../../shared/services/Storage.service'
import UserProfileService from './../../shared/services/UserProfile.service'
import PushDeviceService from './../../shared/services/PushDevice.service'
import PushMessageService from './../../shared/services/PushMessage.service'
import SystemSettingStore from './../../shared/stores/systemSetting.store'

const DEVICE_UID_KEY = 'usabg:push:deviceUid'
const SNOOZE_KEY = 'usabg:push:snoozedUntil'

// Browser permission cannot be revoked programmatically — once granted it stays
// granted. So "off" has to be remembered by us, or every page load would see
// `granted`, silently re-enrol, and turn notifications back on behind the
// agent's back.
const OPTED_OUT_KEY = 'usabg:push:optedOut'

// How long "Not now" suppresses the soft-ask. Long enough not to nag, short
// enough that someone who dismissed it once still gets another chance.
const SNOOZE_DAYS = 14

// Dedicated, deliberately narrow scope. See public/firebase-messaging-sw.js for
// why this must never be "/" — the Workbox cache-busting worker owns that.
// This exact string is DEFAULT_SW_SCOPE in @firebase/messaging.
const SW_SCOPE = '/firebase-cloud-messaging-push-scope'
const SW_PATH = '/firebase-messaging-sw.js'

/**
 * FCM's isSupported() is async in v9+, but the capability check must stay
 * synchronous: enable() calls Notification.requestPermission() from a user
 * gesture, and iOS Safari drops the gesture across an await. So resolve it once
 * here; sync() awaits it before its first check, and enable() only runs after.
 */
let messagingSupported = false
const messagingSupportedReady = isSupported()
  .then(supported => {
    messagingSupported = !!supported
  })
  .catch(() => {
    messagingSupported = false
  })

// Only call once messagingSupported is true: getMessaging() on an unsupported
// browser raises an unhandled rejection rather than throwing.
let messagingInstance = null
const getMessagingInstance = () => {
  if (!messagingInstance) messagingInstance = getMessaging(firebaseApp)
  return messagingInstance
}

/**
 * `src/environments` is gitignored and hand-maintained per machine, so a stale
 * copy may have no messaging config at all. Read defensively — a missing key
 * must leave push inert, never throw during app boot.
 */
const getFirebaseConfig = () => {
  try {
    return (env && env.integrations && env.integrations.firebase && env.integrations.firebase.config) || {}
  } catch (ex) {
    return {}
  }
}

const getVapidKey = () => {
  const config = getFirebaseConfig()
  if (config.vapidKey) return config.vapidKey
  // Tolerated alternate location, in case a copy nests it under `messaging`.
  try {
    return (env.integrations.firebase.messaging || {}).vapidKey || ''
  } catch (ex) {
    return ''
  }
}

const uuid = () => {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID()
    if (window.crypto && window.crypto.getRandomValues) {
      const bytes = window.crypto.getRandomValues(new Uint8Array(16))
      bytes[6] = (bytes[6] & 0x0f) | 0x40
      bytes[8] = (bytes[8] & 0x3f) | 0x80
      const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0'))
      return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex
        .slice(8, 10)
        .join('')}-${hex.slice(10, 16).join('')}`
    }
  } catch (ex) {}
  // Last resort: still unique enough for a per-browser key, and the server
  // treats device_uid as opaque.
  return `f${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`
}

/**
 * Resolve once the REST client has a bearer token to send.
 *
 * On login, Firebase signs in (firing onAuthStateChanged) BEFORE
 * AuthService.obtainToken() has stored the ID token — that waits on a
 * getIdToken(true) round trip. The REST client reads the token from storage, so
 * a request sent in that gap goes out as "Bearer null" and is refused with a
 * 403. On a plain reload the token is already stored and this resolves at once.
 *
 * Resolves (never rejects) on timeout: the REST client's refresh-and-retry is
 * still there as a fallback.
 */
const waitForRestToken = (timeoutMs = 10000, intervalMs = 100) =>
  new Promise(resolve => {
    if (StorageService.getToken()) return resolve()
    const startedAt = Date.now()
    const timer = setInterval(() => {
      if (StorageService.getToken() || Date.now() - startedAt >= timeoutMs) {
        clearInterval(timer)
        resolve()
      }
    }, intervalMs)
  })

/**
 * Resolve once a registration has an ACTIVATED worker.
 *
 * Resolves (rather than rejects) on timeout: getToken() will then fail with its
 * own, more specific error, which is more useful than one invented here.
 */
const waitForActivation = (registration, timeoutMs = 10000) =>
  new Promise(resolve => {
    if (!registration || registration.active) return resolve(registration)

    const worker = registration.installing || registration.waiting
    if (!worker) return resolve(registration)

    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      worker.removeEventListener('statechange', onStateChange)
      clearTimeout(timer)
      resolve(registration)
    }

    function onStateChange() {
      // 'redundant' means this worker was replaced or failed — stop waiting and
      // let getToken() surface whatever the real problem is.
      if (worker.state === 'activated' || worker.state === 'redundant') finish()
    }

    const timer = setTimeout(finish, timeoutMs)
    worker.addEventListener('statechange', onStateChange)
  })

const isIOS = () => {
  try {
    const ua = navigator.userAgent || ''
    if (/iPad|iPhone|iPod/.test(ua)) return true
    // iPadOS 13+ reports itself as a Mac; touch points give it away.
    return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
  } catch (ex) {
    return false
  }
}

const detectPlatform = () => {
  try {
    const ua = navigator.userAgent || ''
    if (isIOS()) return 'ios'
    if (/Android/i.test(ua)) return 'android'
    if (/Macintosh|Mac OS X/i.test(ua)) return 'macos'
    if (/Windows/i.test(ua)) return 'windows'
    if (/Linux|X11/i.test(ua)) return 'linux'
  } catch (ex) {}
  return 'other'
}

const detectBrowser = () => {
  try {
    const ua = navigator.userAgent || ''
    // Order matters — several browsers impersonate Chrome/Safari in their UA.
    if (/Edg\//.test(ua)) return 'Edge'
    if (/OPR\//.test(ua)) return 'Opera'
    if (/Firefox\//.test(ua) || /FxiOS/.test(ua)) return 'Firefox'
    if (/CriOS/.test(ua)) return 'Chrome'
    if (/Chrome\//.test(ua)) return 'Chrome'
    if (/Safari\//.test(ua)) return 'Safari'
  } catch (ex) {}
  return 'Browser'
}

const PLATFORM_LABELS = {
  ios: 'iPhone/iPad',
  android: 'Android',
  macos: 'Mac',
  windows: 'Windows',
  linux: 'Linux',
  other: 'Device',
}

const isStandalone = () => {
  try {
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true
    return navigator.standalone === true
  } catch (ex) {
    return false
  }
}

const browserPermission = () => {
  try {
    if (typeof Notification === 'undefined') return 'unsupported'
    return Notification.permission
  } catch (ex) {
    return 'unsupported'
  }
}

class PushNotificationsStore {
  constructor() {
    makeAutoObservable(this)
  }

  /** @type {'unknown'|'unsupported'|'ios-needs-install'|'default'|'granted'|'denied'|'error'} */
  status = 'unknown'
  /** @type {boolean} true once init() has run to completion at least once */
  isReady = false
  /** @type {boolean} an enable()/disable() call is in flight */
  isBusy = false
  /** @type {string|null} last error surfaced to the UI */
  error = null
  /** @type {object|null} most recent foreground message payload */
  lastMessage = null
  /** @type {number} increments per foreground message, so two identical
   *  messages still register as two events for observers */
  messageTick = 0
  /** @type {Array} this agent's known browser profiles */
  devices = []
  /** @type {boolean} */
  isLoadingDevices = false
  /** @type {number} bumped on snooze so observers re-evaluate shouldOfferEnable */
  snoozeTick = 0
  /**
   * Dismissal that lasts only until the next load. Used for the iOS install
   * coach: unlike a permission prompt, it costs nothing to show again and it is
   * the ONLY route to push on iOS — so a 14-day silence would strand the very
   * people who tapped it meaning "understood, doing it now".
   * @type {boolean}
   */
  isDismissedThisSession = false
  /** @type {boolean} the agent explicitly switched push off on this device */
  isOptedOut = false
  /**
   * Post-enable self-test. The ONLY way to catch OS-level suppression: if macOS
   * or Windows is blocking the browser, permission still reads "granted" and
   * showNotification() resolves silently. So we show one and ask.
   * @type {'idle'|'pending'|'passed'|'failed'}
   */
  selfTest = 'idle'

  _deviceUid = null
  _authUnsubscribe = null
  _lastFirebaseUid = null
  _messageUnsubscribe = null
  _initialised = false
  _hasAttachedRegistration = false

  // ---------------------------------------------------------------- getters

  get isSupported() {
    return this.status !== 'unsupported' && this.status !== 'disabled'
  }

  /**
   * On for this device. Browser permission alone is not enough — the agent may
   * have switched it off here while the OS-level grant remains.
   */
  get isEnabled() {
    return this.status === 'granted' && !this.isOptedOut
  }

  /** True when the only thing standing between the agent and push is an iOS install. */
  get needsHomeScreenInstall() {
    return this.status === 'ios-needs-install'
  }

  get isBlocked() {
    return this.status === 'denied'
  }

  /** True when it is meaningful to show an "enable" affordance. */
  get canPrompt() {
    return this.status === 'default'
  }

  /** True while a "Not now" dismissal is still in effect. */
  get isSnoozed() {
    // Read snoozeTick so this recomputes after snooze() (localStorage is not
    // observable on its own).
    void this.snoozeTick
    try {
      const until = StorageService.get(SNOOZE_KEY)
      if (!until) return false
      const ts = Date.parse(until)
      return !isNaN(ts) && ts > Date.now()
    } catch (ex) {
      return false
    }
  }

  /**
   * Should the dashboard show the soft-ask? Only when something the agent can
   * actually act on is pending — never while impersonating, never once denied
   * (the OS will not re-prompt), never after a recent dismissal.
   */
  get shouldOfferEnable() {
    if (!this.isReady) return false
    if (UserProfileService.isAssumed()) return false
    if (this.isDismissedThisSession) return false
    if (this.isSnoozed) return false
    return this.status === 'default' || this.status === 'ios-needs-install'
  }

  get deviceUid() {
    return this._deviceUid
  }

  get deviceLabel() {
    return `${PLATFORM_LABELS[detectPlatform()] || 'Device'} · ${this.browserName}`
  }

  /** e.g. 'Chrome' — used to pick the right unblock instructions. */
  get browserName() {
    return detectBrowser()
  }

  // ------------------------------------------------------------- lifecycle

  /**
   * Idempotent. Subscribes to auth so enrolment follows login, logout, and any
   * change of identity. Safe to call before the user has signed in.
   */
  init = () => {
    if (this._initialised) return
    this._initialised = true

    runInAction(() => {
      this._deviceUid = this._ensureDeviceUid()
      this.isOptedOut = this._readOptedOut()
    })

    try {
      this._authUnsubscribe = onAuthStateChanged(firebaseAuth, async user => {
        const uid = (user && user.uid) || null

        // Identity changed on this browser (logout, or a different account
        // signing in). Drop the previous binding before doing anything else.
        //
        // AWAITED on purpose: on a direct A -> B switch (no null in between)
        // an un-awaited teardown would race sync(), and its revoke could land
        // after B's register — silently revoking the row we had just created
        // for B, so B would appear enrolled but never receive anything.
        if (this._lastFirebaseUid && this._lastFirebaseUid !== uid) {
          await this._teardownToken()
        }
        this._lastFirebaseUid = uid

        if (uid) {
          await waitForRestToken()
          await this.sync()
          // After auth, so the click report goes out authenticated.
          this.reportClickFromUrl()
        } else {
          runInAction(() => {
            this.isReady = true
          })
        }
      })
    } catch (ex) {
      console.error('[push] auth subscription failed', ex)
      runInAction(() => {
        this.status = 'unsupported'
        this.isReady = true
      })
    }
  }

  dispose = () => {
    if (typeof this._authUnsubscribe === 'function') this._authUnsubscribe()
    if (typeof this._messageUnsubscribe === 'function') this._messageUnsubscribe()
    this._authUnsubscribe = null
    this._messageUnsubscribe = null
    this._initialised = false
  }

  /**
   * Reconcile this browser's state with the server. Called on every load.
   * Silent: obtains a token only if permission is ALREADY granted, and never
   * prompts.
   */
  sync = async () => {
    // Never bind a device token while an admin is impersonating an agent.
    // The server enforces this structurally; this just avoids the round trip.
    if (UserProfileService.isAssumed()) {
      runInAction(() => {
        this.isReady = true
      })
      return
    }

    await messagingSupportedReady
    const state = this._evaluateCapability()
    runInAction(() => {
      this.status = state
      this.isOptedOut = this._readOptedOut()
    })

    // Channel switched off org-wide, or explicitly switched off on this device.
    // Either way: do not enrol, and do not touch the server row — the existing
    // readiness record stays accurate for when the switch is flipped back.
    if (state === 'disabled' || this.isOptedOut) {
      runInAction(() => {
        this.isReady = true
      })
      return
    }

    if (state === 'granted') {
      await this._enrol()
      return
    }

    // Not granted — still report the device so BST can distinguish "blocked"
    // from "never asked" from "never opened the backoffice".
    await this._report(state, null)
    runInAction(() => {
      this.isReady = true
    })
  }

  /**
   * Ask the browser for permission. MUST be called from a user gesture.
   */
  enable = async () => {
    if (this.isBusy) return this.status
    runInAction(() => {
      this.isBusy = true
      this.error = null
    })

    try {
      const capability = this._evaluateCapability()
      // None of these can be resolved by prompting: the browser cannot do it,
      // iOS needs an install first, or BST has the channel switched off.
      if (capability === 'unsupported' || capability === 'ios-needs-install' || capability === 'disabled') {
        runInAction(() => {
          this.status = capability
        })
        return capability
      }

      // Turning it on clears any previous opt-out on this device.
      this._writeOptedOut(false)
      runInAction(() => {
        this.isOptedOut = false
      })

      const permission = await Notification.requestPermission()
      runInAction(() => {
        this.status = permission
      })

      // unsuppress: this is the explicit user gesture that is allowed to clear
      // a previous "off", server-side as well as locally.
      if (permission === 'granted') {
        await this._enrol({ unsuppress: true })
        // Immediately prove it actually shows up. See runSelfTest().
        if (this.status === 'granted') this.runSelfTest()
      } else {
        await this._report(permission, null, { unsuppress: true })
      }

      return permission
    } catch (ex) {
      console.error('[push] enable failed', ex)
      runInAction(() => {
        this.status = 'error'
        this.error = 'We could not turn on notifications for this device.'
      })
      return 'error'
    } finally {
      runInAction(() => {
        this.isBusy = false
      })
      this.loadDevices()
    }
  }

  /**
   * Turn push off for this browser. Deletes the token at FCM so the device
   * cannot receive even if a stale server row survives.
   */
  disable = async () => {
    if (this.isBusy) return
    runInAction(() => {
      this.isBusy = true
      this.error = null
    })

    try {
      // Remember the choice BEFORE tearing down, so a failure mid-way still
      // leaves this device off rather than silently re-enrolling next load.
      this._writeOptedOut(true)
      runInAction(() => {
        this.isOptedOut = true
      })

      // Deletes the token at FCM and SUPPRESSES the server row. Deliberately
      // does NOT call _report() afterwards — register() clears revoked_at, so
      // reporting here would immediately undo it.
      await this._teardownToken({ suppress: true })
    } catch (ex) {
      console.error('[push] disable failed', ex)
      runInAction(() => {
        this.error = 'We could not turn off notifications for this device.'
      })
    } finally {
      runInAction(() => {
        this.isBusy = false
      })
      this.loadDevices()
    }
  }

  _readOptedOut = () => {
    try {
      return `${StorageService.get(OPTED_OUT_KEY)}` === '1'
    } catch (ex) {
      return false
    }
  }

  _writeOptedOut = value => {
    try {
      if (value) StorageService.set(OPTED_OUT_KEY, '1')
      else StorageService.clear(OPTED_OUT_KEY)
    } catch (ex) {}
  }

  /**
   * Raise a real OS notification for a message that arrived while the page was
   * in the foreground.
   *
   * FCM's definition of "foreground" is narrower than intuition: it checks
   * `client.visibilityState === 'visible'`, which stays true when the agent
   * switches to a different APPLICATION — the tab is still the active tab in
   * its window. So an agent working in their email client, with the back office
   * merely sitting behind it, gets the in-page path and would otherwise see
   * only a toast they are not looking at.
   *
   * The service worker does not double up here: when a visible client exists it
   * hands the payload to the page and returns without showing anything.
   */
  showForegroundNotification = async data => {
    if (!data) return
    try {
      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
      const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE)
      if (!reg) return
      await reg.showNotification(data.title || 'USA Benefits Group', {
        body: data.body || '',
        icon: '/icons/icon-192.png',
        // Same tag the worker uses, so a message can never surface twice.
        tag: `usabg-push-${data.id || 'unknown'}`,
        renotify: true,
        requireInteraction: `${data.severity}` === 'critical',
        data: { url: data.url || '/account-alerts', id: data.id || null },
      })
    } catch (ex) {
      console.error('[push] foreground notification failed', ex)
    }
  }

  /** Platform slug, for picking the right OS-level instructions. */
  get platform() {
    return detectPlatform()
  }

  /**
   * Show a notification and ask whether it was actually seen.
   *
   * There is no API for "the OS is swallowing our notifications" — permission
   * reads granted, showNotification() resolves, and nothing appears. Asking the
   * person is the only detection method that exists.
   */
  runSelfTest = async () => {
    if (this.status !== 'granted') return
    runInAction(() => {
      this.selfTest = 'pending'
    })
    try {
      const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE)
      if (!reg) throw new Error('no service worker registration')
      await reg.showNotification('Notifications are on', {
        body: 'This is a test — you can dismiss it.',
        icon: '/icons/icon-192.png',
        tag: 'usabg-push-selftest',
        renotify: true,
      })
    } catch (ex) {
      console.error('[push] self-test failed to display', ex)
      runInAction(() => {
        this.selfTest = 'failed'
      })
    }
  }

  /** @param {boolean} sawIt — the agent's answer. */
  confirmSelfTest = async sawIt => {
    try {
      const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE)
      if (reg) (await reg.getNotifications({ tag: 'usabg-push-selftest' })).forEach(n => n.close())
    } catch (ex) {}

    // Persist to the SERVER, not just localStorage — this is the signal BST
    // uses to tell a genuinely reachable agent from one whose OS is silently
    // swallowing everything. Kept client-side it would be invisible to them.
    try {
      if (this._deviceUid) await PushDeviceService.verify(this._deviceUid, sawIt)
    } catch (ex) {
      console.error('[push] failed to record verification', ex)
    }
    this.loadDevices()

    // The answer is kept server-side only (verified_at / verify_failed_at on the
    // device row, written just above). A localStorage copy was written here and
    // never read anywhere — and it could not have helped: it is per-browser, so
    // clearing site data would silently erase the one signal that an agent is
    // unreachable. currentDeviceRow reads the server instead.
    if (sawIt) {
      runInAction(() => {
        this.selfTest = 'passed'
      })
      return
    }
    // Deliberately do NOT disable push. The agent may fix their OS settings in
    // a moment, and turning it off would lose the enrolment they just made.
    runInAction(() => {
      this.selfTest = 'failed'
    })
  }

  /**
   * Report that the agent arrived here by tapping a notification.
   *
   * The service worker tags its click URL with `?push=<id>`; it cannot call the
   * API itself (no auth token, no App Check token), so the authenticated page
   * reports on its behalf. Returns true if a param was consumed.
   */
  reportClickFromUrl = async () => {
    let id = null
    try {
      id = new URLSearchParams(window.location.search).get('push')
    } catch (ex) {
      return false
    }
    if (!id || !/^\d+$/.test(id)) return false

    try {
      await PushMessageService.reportClick(id, this._deviceUid)
    } catch (ex) {
      console.error('[push] failed to report click', ex)
    }

    // Strip the param so a refresh or a shared link cannot re-report it.
    try {
      const url = new URL(window.location.href)
      url.searchParams.delete('push')
      window.history.replaceState({}, '', url.pathname + url.search + url.hash)
    } catch (ex) {}

    return true
  }

  dismissSelfTest = () => {
    runInAction(() => {
      this.selfTest = 'idle'
    })
  }

  /** Dismiss the soft-ask for SNOOZE_DAYS. */
  snooze = () => {
    // The iOS coach is instructions, not an ask — dismissing it must not cost
    // two weeks of silence. Installing to the Home Screen is a multi-step trip
    // out of the browser, so the common case is "dismiss, get interrupted,
    // come back"; a persisted snooze would meet that return with nothing.
    if (this.needsHomeScreenInstall) {
      runInAction(() => {
        this.isDismissedThisSession = true
      })
      return
    }

    try {
      const until = new Date(Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000)
      StorageService.set(SNOOZE_KEY, until.toISOString())
    } catch (ex) {}
    runInAction(() => {
      this.snoozeTick += 1
    })
  }

  /** Every browser profile this agent has opened the back office in. */
  loadDevices = async () => {
    const userId = UserProfileService.getUserId()
    if (!userId) return []

    runInAction(() => {
      this.isLoadingDevices = true
    })
    try {
      const result = await PushDeviceService.search({
        search: { user_id: userId },
        order_by: { last_seen_at: 'DESC' },
        pagination: false,
      })
      const models = (result && result.models) || (Array.isArray(result) ? result : [])
      runInAction(() => {
        this.devices = models
      })
      return models
    } catch (ex) {
      console.error('[push] loadDevices failed', ex)
      runInAction(() => {
        this.devices = []
      })
      return []
    } finally {
      runInAction(() => {
        this.isLoadingDevices = false
      })
    }
  }

  /**
   * Forget a browser profile. If it is THIS browser, also invalidate the token
   * at FCM. For any other device we can only drop our record — that browser
   * still holds a granted permission, so it will re-appear the next time it is
   * used. The UI says so rather than implying a remote switch-off.
   */
  removeDevice = async deviceUid => {
    if (!deviceUid) return
    if (deviceUid === this._deviceUid) {
      await this.disable()
    } else {
      // Suppress, not just revoke. A plain revoke would be undone the moment
      // that browser next checked in, because its permission is still granted.
      try {
        await PushDeviceService.suppress(deviceUid)
      } catch (ex) {
        console.error('[push] removeDevice failed', ex)
      }
    }
    await this.loadDevices()
  }

  /** True for the row describing the browser this code is running in. */
  isCurrentDevice = device => !!device && !!this._deviceUid && `${device.device_uid}` === `${this._deviceUid}`

  /**
   * This device's server row, or null.
   *
   * The server is the only durable record of a failed self-test: `selfTest`
   * lives in memory and resets to 'idle' on every load, so without this the
   * warning vanishes on the next refresh and the panel goes back to claiming
   * notifications are working.
   */
  get currentDeviceRow() {
    return (this.devices || []).find(d => this.isCurrentDevice(d)) || null
  }

  /**
   * True when the agent has told us notifications do not appear here and
   * nothing has proved otherwise since. Drives the persistent OS-settings
   * warning on /account-alerts.
   */
  get isSilentlySuppressed() {
    const row = this.currentDeviceRow
    return !!row && !!row.verify_failed_at && !row.last_clicked_at
  }

  /**
   * Release this browser's token on sign-out.
   *
   * Distinct from disable(): signing out is NOT a preference. It must not set
   * the opt-out flag, or every logout would leave the device permanently muted
   * until someone re-enabled it by hand. It only invalidates the credential so
   * a message cannot ring on a machine the agent has signed out of.
   */
  releaseForLogout = async () => {
    try {
      await this._teardownToken()
    } catch (ex) {
      console.error('[push] logout release failed', ex)
    }
    runInAction(() => {
      this.status = 'unknown'
      this.isReady = false
      this.devices = []
    })
  }

  // ---------------------------------------------------------------- internals

  _ensureDeviceUid = () => {
    let uid = null
    try {
      uid = StorageService.get(DEVICE_UID_KEY)
    } catch (ex) {}
    if (!uid || !/^[A-Za-z0-9-]{8,64}$/.test(`${uid}`)) {
      uid = uuid()
      try {
        StorageService.set(DEVICE_UID_KEY, uid)
      } catch (ex) {}
    }
    return uid
  }

  /**
   * What is possible in THIS browser right now — before any prompting.
   */
  /**
   * BST kill switch (`push---enabled` system setting).
   *
   * FAIL OPEN: absent or malformed means enabled. A missing seed must never
   * silently disable a channel people rely on — only an explicit
   * {"enabled": false} turns it off.
   */
  get isKillSwitched() {
    try {
      const setting = SystemSettingStore.get('push---enabled')
      return !!setting && setting.enabled === false
    } catch (ex) {
      return false
    }
  }

  _evaluateCapability = () => {
    try {
      if (this.isKillSwitched) return 'disabled'
      if (!('serviceWorker' in navigator)) return 'unsupported'
      // No key configured for this environment: the feature is simply off.
      if (!getVapidKey()) return 'unsupported'

      // MUST come before the Notification/PushManager/isSupported probes.
      // iOS only exposes push to a site installed on the Home Screen, and until
      // it is, Safari exposes none of those three — so probing them first reads
      // an installable iPhone as permanently unsupported and buries the install
      // coaching this branch exists to trigger.
      if (isIOS() && !isStandalone()) return 'ios-needs-install'

      if (typeof Notification === 'undefined') return 'unsupported'
      if (!('PushManager' in window)) return 'unsupported'
      if (!messagingSupported) return 'unsupported'

      return browserPermission()
    } catch (ex) {
      console.error('[push] capability check failed', ex)
      return 'unsupported'
    }
  }

  /**
   * Register the dedicated worker, obtain a token, and report it.
   */
  _enrol = async ({ unsuppress } = {}) => {
    try {
      const registration = await this._registerServiceWorker()
      const messaging = getMessagingInstance()

      const token = await getToken(messaging, {
        vapidKey: getVapidKey(),
        serviceWorkerRegistration: registration,
      })
      // getToken() has now attached OUR registration to the messaging instance,
      // which is what makes deleteToken() safe (see _teardownToken).
      this._hasAttachedRegistration = true

      if (!token) {
        await this._report('granted', null, { unsuppress })
        return null
      }

      await this._report('granted', token, { unsuppress })
      this._subscribeToForeground(messaging)
      runInAction(() => {
        this.status = 'granted'
        this.isReady = true
      })
      return token
    } catch (ex) {
      console.error('[push] enrolment failed', ex)
      runInAction(() => {
        this.status = 'error'
        this.error = 'Notifications could not be set up on this device.'
        this.isReady = true
      })
      return null
    }
  }

  /**
   * Register at the NARROW scope, explicitly. Never "/" — that belongs to the
   * Workbox cache-busting worker, and registering a different script there
   * would replace it.
   *
   * `updateViaCache: 'none'` keeps the browser from serving this script from
   * the HTTP cache, complementing the no-cache header in firebase.json.
   */
  _registerServiceWorker = async () => {
    const config = getFirebaseConfig()
    const params = new URLSearchParams({
      apiKey: config.apiKey || '',
      projectId: config.projectId || '',
      messagingSenderId: config.messagingSenderId || '',
      appId: config.appId || '',
    })

    // Stamp the app version into the script URL.
    //
    // This worker draws every notification, so a stale copy means a fix never
    // reaches the agent. Nothing normally forces a refresh: no page ever
    // navigates into its scope, so the browser's own update check is the ~24h
    // heuristic. A changed script URL, on the other hand, is an unconditional
    // update — and the release process already bumps this version, so shipping
    // a worker fix is enough to guarantee it lands.
    //
    // Same scope, so this updates the existing registration rather than
    // creating a second one.
    let appVersion = 'dev'
    try {
      if (typeof global !== 'undefined' && global.appVersion) appVersion = `${global.appVersion}`
    } catch (ex) {}
    params.set('v', appVersion)

    const registration = await navigator.serviceWorker.register(`${SW_PATH}?${params.toString()}`, {
      scope: SW_SCOPE,
      updateViaCache: 'none',
    })

    // Belt and braces: force an update check rather than relying on the
    // browser's schedule. Cheap, and never rejects in a way we care about.
    try {
      await registration.update()
    } catch (ex) {
      console.warn('[push] worker update check failed (continuing)', ex)
    }

    // register() resolves as soon as the REGISTRATION exists — the worker may
    // still be `installing`. getToken() then calls pushManager.subscribe(),
    // which requires an ACTIVATED worker, and throws
    //   AbortError: Subscription failed - no active Service Worker
    // if it is not ready yet.
    //
    // This bites hardest on FIRST enrolment: before the agent grants permission
    // there is no worker at all, so clicking "Turn on" registers and subscribes
    // back to back. Whether it wins the race depends on how fast the script
    // installs — which makes it look like flaky push rather than a bug.
    //
    // navigator.serviceWorker.ready is NOT usable here: it resolves for the
    // registration CONTROLLING the page, and this one deliberately controls
    // nothing (narrow scope, so it cannot displace the Workbox worker).
    await waitForActivation(registration)

    return registration
  }

  _subscribeToForeground = messaging => {
    if (this._messageUnsubscribe) return
    try {
      this._messageUnsubscribe = onMessage(messaging, payload => {
        // The background worker does not run while a tab is focused, so without
        // this a message arriving mid-session would be completely invisible.
        //
        // This store deliberately does NOT render the message itself. Keeping
        // toast/bell concerns out of here keeps react-toastify and
        // UserAlerts.store out of its import graph — which matters because
        // Auth.service imports this store, and those would close an import
        // cycle back through the API client. PushNotificationsBootstrap
        // observes `messageTick` and does the rendering.
        runInAction(() => {
          this.lastMessage = (payload && payload.data) || null
          this.messageTick += 1
        })
      })
    } catch (ex) {
      console.error('[push] foreground subscription failed', ex)
    }
  }

  /**
   * Upsert this browser's row. Carries no user id — the server derives identity
   * from the Firebase ID token.
   */
  _report = async (permissionState, token, { unsuppress } = {}) => {
    if (UserProfileService.isAssumed()) return null
    try {
      const row = await PushDeviceService.register({
        device_uid: this._deviceUid,
        fcm_token: token || null,
        permission_state: permissionState,
        is_standalone: isStandalone() ? 1 : 0,
        platform: detectPlatform(),
        browser: detectBrowser(),
        label: this.deviceLabel,
        // Only ever true from enable(), a real user gesture. The silent sync
        // path must never clear a suppression.
        ...(unsuppress ? { unsuppress: 1 } : {}),
      })

      // The server is authoritative on "off". Mirror it locally so a device
      // whose localStorage was cleared still reports itself as switched off.
      if (row && typeof row === 'object') {
        const serverSuppressed = !!row.suppressed_at
        runInAction(() => {
          this.isOptedOut = serverSuppressed || this._readOptedOut()
        })
        if (serverSuppressed) this._writeOptedOut(true)
      }

      return row
    } catch (ex) {
      // Readiness reporting is best-effort; never block the app on it.
      console.error('[push] device report failed', ex)
      return null
    }
  }

  /**
   * Invalidate the token at FCM and soft-revoke the server row. Used on logout,
   * on identity change, and when the agent switches push off.
   */
  _teardownToken = async ({ suppress } = {}) => {
    // Only after getToken() attached our registration this session. Without
    // one, deleteToken() calls FCM's registerDefaultSw(), which registers a
    // bare /firebase-messaging-sw.js (no config params) at OUR scope and
    // replaces the working worker. With no registration attached there is no
    // token from this session to delete; the server revoke below still runs.
    try {
      if (messagingSupported && this._hasAttachedRegistration) {
        await deleteToken(getMessagingInstance())
        this._hasAttachedRegistration = false
      }
    } catch (ex) {
      // Token may already be gone; revoking the server row still matters.
      console.warn('[push] deleteToken failed (continuing)', ex)
    }

    // Only worth attempting while we still hold a valid Firebase token. On
    // sign-out this runs twice — once from AuthService.logout() (authenticated,
    // the one that counts) and again from onAuthStateChanged(null). Without
    // this guard the second pass would log an authentication error on every
    // single logout, which is exactly the kind of noise someone later wastes an
    // afternoon chasing.
    try {
      const isAuthenticated = !!firebaseAuth.currentUser
      if (this._deviceUid && isAuthenticated) {
        // suppress = the agent chose "off" and it must survive them clearing
        // site data. Plain revoke = sign-out, which is not a preference.
        if (suppress) await PushDeviceService.suppress(this._deviceUid)
        else await PushDeviceService.revoke(this._deviceUid)
      }
    } catch (ex) {
      console.error('[push] revoke failed', ex)
    }

    if (typeof this._messageUnsubscribe === 'function') {
      this._messageUnsubscribe()
      this._messageUnsubscribe = null
    }
  }
}

export default new PushNotificationsStore()
